import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CustomInput from "../../../../commonComponents/CustomInput";
import CustomDropdown from "../../../../commonComponents/CustomDropdown";
import CustomDatePicker from "../../../../commonComponents/CustomDatePicker";
import ValidIcon from "../../../../assets/icons/emergency_home.svg";
import { getPincodeDetails } from "../../../../store/OnboardingSlice/thunk";
import {
  getEducationDegreeLabel,
  getEducationQualificationLabel,
} from "../../../../utils/educationHelpers";
import {
  getHrEditOptions,
  getHrEducationMasterField,
  HR_REASON_REQUIRED_MESSAGE,
  isCompletePincode,
  normalizePincode,
} from "../../../../utils/hrDirectEdit";
import {
  extractOnboardingActionData,
  mapPincodeDetailsFromData,
} from "../../../../utils/onboardingHelpers";
import EducationMasterField from "./EducationMasterField";

const ANY_VALUE = /^[\s\S]*$/;

const toPickerDate = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD MMM YYYY", moment.ISO_8601],
    true,
  );
  if (parsed.isValid()) return parsed.toDate();
  const loose = moment(value);
  return loose.isValid() ? loose.toDate() : "";
};

const TextControl = ({ label, value, isRequired, onChange, validationStr }) => (
  <CustomInput
    label={label}
    value={value ?? ""}
    placeholder={`Enter ${String(label || "").toLowerCase()}`}
    isRequired={!!isRequired}
    editable
    readOnly={false}
    preview
    validationStr={validationStr || ANY_VALUE}
    onChange={onChange}
  />
);

const DropdownControl = ({ label, value, options, isRequired, onChange }) => (
  <CustomDropdown
    label={label}
    value={value || ""}
    options={options}
    isRequired={!!isRequired}
    showOptional={!isRequired}
    placeholder={`Select ${String(label || "").toLowerCase()}`}
    onSelectOption={(option) => onChange(option)}
  />
);

const DateControl = ({ label, value, isRequired, onChange }) => (
  <CustomDatePicker
    label={label}
    value={toPickerDate(value)}
    isRequired={!!isRequired}
    showOptional={!isRequired}
    placeholderText="Select a date"
    allowPastDate
    allowFutureDate
    onChange={(next) =>
      onChange(next ? moment(next).format("YYYY-MM-DD") : "")
    }
  />
);

export const HrEditReasonField = ({ value, onChange, showError }) => {
  const text = value ?? "";
  const missing = !String(text).trim();
  const visuallyEmpty = String(text) === "";

  return (
    <div
      className={`rr-hr-editor-reason${
        showError && missing ? " rr-hr-reason--invalid" : ""
      }`}
    >
      <CustomInput
        label="Reason for Edit"
        value={text}
        placeholder="Why is this being changed?"
        isRequired
        editable
        readOnly={false}
        preview
        validationStr={ANY_VALUE}
        isError={showError && missing && visuallyEmpty}
        additionalValText={HR_REASON_REQUIRED_MESSAGE}
        onChange={onChange}
      />
      {showError && missing && !visuallyEmpty ? (
        <div className="valid-text flex items-center">
          <img src={ValidIcon} alt="" style={{ marginRight: "4px" }} />
          <div className="valid-text">{HR_REASON_REQUIRED_MESSAGE}</div>
        </div>
      ) : null}
    </div>
  );
};

const PincodeLookup = ({ fieldKey, value, onApply }) => {
  const dispatch = useDispatch();
  const normalized = normalizePincode(value);
  const lastFetchedRef = useRef(normalized);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!isCompletePincode(normalized) || lastFetchedRef.current === normalized) {
      setStatus("");
      return undefined;
    }

    let cancelled = false;
    setStatus("Looking up city and state...");
    dispatch(getPincodeDetails({ pincode: normalized }))
      .unwrap()
      .then((response) => {
        if (cancelled) return;
        const data = extractOnboardingActionData(response);
        if (data == null) {
          throw new Error("Request failed");
        }
        lastFetchedRef.current = normalized;
        onApply(fieldKey, normalized, mapPincodeDetailsFromData(data));
        setStatus("");
      })
      .catch(() => {
        if (cancelled) return;
        lastFetchedRef.current = "";
        setStatus("");
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, fieldKey, normalized, onApply]);

  if (!status) return null;
  return <div className="rr-hr-lookup-status">{status}</div>;
};

const FlaggedField = ({ children, flagRemark, flagWithRemark = false }) => (
  <div className="rr-hr-editor-field">
    <div className="rr-hr-editor-control">
      {children}
      {flagRemark != null && !flagWithRemark ? (
        <span className="material-symbols-rounded rr-field-flagged-icon" aria-hidden="true">
          flag
        </span>
      ) : null}
    </div>
    {flagRemark != null ? (
      <div
        className={`rr-field-flag-comment${
          flagWithRemark ? " rr-field-flag-comment--with-icon" : ""
        }`}
      >
        {flagWithRemark ? (
          <span className="material-symbols-rounded rr-field-flagged-icon" aria-hidden="true">
            flag
          </span>
        ) : null}
        <span>{flagRemark}</span>
      </div>
    ) : null}
  </div>
);

const HrFieldEditor = ({
  session,
  flagRemark = null,
  onChangeValue,
  onApplyPincode,
  onChangeReason,
  onCancel,
  onSave,
}) => {
  if (!session || session.kind !== "field") return null;

  const { fieldKey, label, isRequired, control, values, reason, error } = session;
  const reasonError = error === HR_REASON_REQUIRED_MESSAGE;
  const degreeLabel =
    fieldKey === "qualification"
      ? getEducationDegreeLabel(values.qualification)
      : null;

  let controlNode = null;
  let companionNode = null;
  let lookupNode = null;
  const masterConfig =
    session.sectionKey === "education"
      ? getHrEducationMasterField(values.qualification, fieldKey)
      : null;
  if (control === "date") {
    controlNode = (
      <DateControl
        label={label}
        value={values[fieldKey]}
        isRequired={isRequired}
        onChange={(value) => onChangeValue(fieldKey, value)}
      />
    );
  } else if (control === "marital" || control === "blood") {
    controlNode = (
      <DropdownControl
        label={label}
        value={values[fieldKey]}
        options={getHrEditOptions(fieldKey)}
        isRequired={isRequired}
        onChange={(value) => onChangeValue(fieldKey, value)}
      />
    );
  } else if (control === "qualification") {
    const options = getHrEditOptions("qualification");
    controlNode = (
      <CustomDropdown
        label={label}
        value={getEducationQualificationLabel(values.qualification)}
        options={options.map((item) => item.label)}
        isRequired
        placeholder="Select qualification"
        onSelectOption={(optionLabel) => {
          const match = options.find((item) => item.label === optionLabel);
          onChangeValue("qualification", match?.key || "");
        }}
      />
    );
    const degreeConfig = getHrEducationMasterField(values.qualification, "degree");
    companionNode = degreeLabel ? (
      degreeConfig ? (
        <EducationMasterField
          label={degreeLabel}
          value={values.degree}
          isRequired
          placeholder={`Select ${degreeLabel.toLowerCase()}`}
          config={degreeConfig}
          onChange={(value) => onChangeValue("degree", value)}
        />
      ) : (
        <TextControl
          label={degreeLabel}
          value={values.degree}
          isRequired
          onChange={(value) => onChangeValue("degree", value)}
        />
      )
    ) : null;
  } else if (
    fieldKey === "current_address_pincode" ||
    fieldKey === "permanent_address_pincode"
  ) {
    controlNode = (
      <TextControl
        label={label}
        value={values[fieldKey]}
        isRequired={isRequired}
        validationStr={/^[0-9]{0,6}$/}
        onChange={(value) => onChangeValue(fieldKey, normalizePincode(value))}
      />
    );
    lookupNode = (
      <PincodeLookup
        fieldKey={fieldKey}
        value={values[fieldKey]}
        onApply={onApplyPincode}
      />
    );
  } else if (masterConfig) {
    controlNode = (
      <EducationMasterField
        label={label}
        value={values[fieldKey]}
        isRequired={isRequired}
        placeholder={
          masterConfig.mode === "search"
            ? `Search ${String(label || "").toLowerCase()}`
            : `Select ${String(label || "").toLowerCase()}`
        }
        config={masterConfig}
        onChange={(value) => onChangeValue(fieldKey, value)}
      />
    );
  } else {
    const validationStr =
      fieldKey === "email_address" || fieldKey === "emergency_email"
        ? /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        : fieldKey === "alternative_phone_number" || fieldKey === "emergency_phone"
          ? /^[0-9+\s-]{10,16}$/
          : fieldKey === "current_address_pincode" ||
              fieldKey === "permanent_address_pincode"
            ? /^[0-9]{6}$/
            : fieldKey === "cgpa_percentage" ||
                fieldKey === "maximum_cgpa_percentage"
              ? /^\d+(\.\d+)?$/
              : ANY_VALUE;
    controlNode = (
      <TextControl
        label={label}
        value={values[fieldKey]}
        isRequired={isRequired}
        validationStr={validationStr}
        onChange={(value) => onChangeValue(fieldKey, value)}
      />
    );
  }

  return (
    <div className="rr-hr-editor">
      <FlaggedField flagRemark={flagRemark} flagWithRemark={control === "date"}>
        {controlNode}
      </FlaggedField>
      {lookupNode}
      {companionNode}
      <HrEditReasonField
        value={reason}
        onChange={onChangeReason}
        showError={reasonError}
      />
      {error && !reasonError ? (
        <div className="rr-hr-editor-error">{error}</div>
      ) : null}
      <div className="rr-hr-editor-actions">
        <button type="button" className="rr-hr-editor-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="rr-hr-editor-save" onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default HrFieldEditor;
